import {BaseSlider} from "../BaseSlider";
import ICustomForm from "../../ICustomForm";
import m, {Vnode, VnodeDOM} from "mithril";
import ICustomFormSettings from "../../ICustomFormSettings";
import Design from "../../../ui/design";
import CheckboxCell from "../../shared/components/FormCell/CheckboxCell";
import LabelCell from "../../shared/components/FormCell/LabelCell";
import {LabelItem} from "../../../model/items/labelItem";
import {SliderItem} from "../../../model/items/slider";
import {CheckboxItem} from "../../../model/items/checkbox";
import SliderBox from "../../shared/components/SliderBox/SliderBox";
import {ImageItem} from "../../../model/items/imageItem";

type TSliderGroup = {
    labelCell: LabelCell|null,
    sliderBox: SliderBox,
    checkboxCells: CheckboxCell[],
};


export default class SliderMultiple extends BaseSlider implements ICustomForm
{
    private readonly representation: "oneByOne"|"all";

    private currentGroup= 0;

    private readonly sliderGroups: TSliderGroup[] = [];

    private otherCheckboxCells: CheckboxCell[] = [];

    constructor(settings: ICustomFormSettings) {
        super(settings);

        // Prepare slider groups
        this.settings.form.grid.layout.forEach((row) => {
            const cellItems = row.map(cell => cell.cellItems[0]);

            // Label
            const labelItemIndex = cellItems.findIndex(ci => ci instanceof LabelItem);
            const labelItem = (labelItemIndex === 0 || labelItemIndex === 1) ? cellItems[labelItemIndex] as LabelItem : undefined;

            // Image
            const imageItem = cellItems.find(ci => ci instanceof ImageItem) as ImageItem;
            const imageUrl = imageItem ? this.settings.form.questionary.getFileUrl(imageItem.file) : undefined

            // Slider
            const sliderItem = cellItems.find(ci => ci instanceof SliderItem) as SliderItem;

            // Checkboxes
            const checkboxItems = cellItems.filter(ci => ci instanceof CheckboxItem) as CheckboxItem[];

            // Skip rows without slider, but not skip checkboxes
            if (!sliderItem) {
                checkboxItems.forEach(ch => this.otherCheckboxCells.push(new CheckboxCell(ch.cell)));
                return;
            }

            // Skip rows with hidden slider
            if (!sliderItem.cell.visible) {
                return;
            }

            // Save group
            this.sliderGroups.push({
                labelCell: labelItem ? new LabelCell(labelItem) : null,
                sliderBox: new SliderBox(sliderItem, sliderItem.labels.left, sliderItem.labels.right, "numeric", imageUrl),
                checkboxCells: checkboxItems.map(ch => new CheckboxCell(ch.cell, (ci, v) => this.onSetValue(ci, v))),
            });
        });

        // choose representation
        this.representation = (
            this.otherCheckboxCells.length || (window.innerWidth >= Design.BC.md && window.innerHeight > 575)
        ) ? "all" : "oneByOne";

        if (this.representation === "oneByOne") {
            this.currentGroup = Math.max(0, this.sliderGroups.findIndex(group => this.isGroupValid(group)) );
        }
    }

    private isGroupValid(group: TSliderGroup): boolean {
        return !group.sliderBox.sliderItem.cell.enabled || Boolean(typeof group.sliderBox.value !== "undefined");
    }

    private existPrevious(): boolean {
        return this.currentGroup !== 0;
    }

    protected existNext(): boolean {
        return this.currentGroup+1 < this.sliderGroups.length;
    }

    protected nextSlider(): void {
        if (this.currentGroup < this.sliderGroups.length-1) {
            this.currentGroup++;
        }
    }

    protected previousSlider(): void {
        if (this.currentGroup > 0) {
            this.currentGroup--;
        }
    }

    protected viewControls(nextButtonText: string = 'nextButton') {
        if (this.representation === "all") {
            return super.viewControls();
        }

        const form = this.settings.form;
        const questionary = form.questionary;

        const existPrevSlider = this.existPrevious();
        const existNextSlider = this.existNext();

        const visiblePreviousButton = this.settings.app.prevEnabled() || existPrevSlider;
        const visibleLangButton = this.settings.app.langSwitcherEnabled();
        const visibleNextButton = this.settings.app.nextEnabled() || existNextSlider;
        const enableNextButton = this.isGroupValid(this.sliderGroups[this.currentGroup]);

        return m(".controls", {},
            m(".control-group", {}, [
                visiblePreviousButton ? m("button.btn.large.outline.secondary#previousButton", {
                    onclick: () => {
                        if (existPrevSlider) {
                            this.previousSlider()
                        } else {
                            this.settings.prevForm()
                        }
                    },
                    title: questionary.renderText(questionary.messages.get('backButton')),
                }, [
                    m('i.icon.icon-chevron-left', {}, '')
                ]) : null,

                visibleLangButton ? m("button.btn.large.outline.secondary#languageButton", {
                    onclick: () => this.settings.selectLanguage(),
                    title: questionary.renderText(questionary.messages.get('language'))
                }, [
                    m('i.icon.icon-translate', {}, '')
                ]) : null,

                visibleNextButton ? m("button.btn.large.raised.primary.has-primary-background#nextButton", {
                    onclick: () => {
                        if (existNextSlider) {
                            this.nextSlider()
                        } else {
                            this.settings.nextForm()
                        }
                    },
                    disabled: enableNextButton ? false : 'disabled',
                }, [
                    m('span', {}, questionary.renderText(questionary.messages.get(nextButtonText)) ),
                    m('i.icon.icon-chevron-right'+(enableNextButton?'.text-light':''), {}, '')
                ]) : null,
            ])
        );
    }

    private viewGroup(group: TSliderGroup): Vnode {
        return m(".sliderGroup", {}, [
            group.labelCell ? m(group.labelCell) : null,
            m(group.sliderBox),
            ...group.checkboxCells.map(checkboxCell => m(checkboxCell)),
        ])
    }

    public view(): Vnode {

        if (this.representation === "all") {
            return this.viewLayout(
                m("form.representation-"+this.representation, {}, [
                    ...this.sliderGroups.map(group => this.viewGroup(group)),
                    ...this.otherCheckboxCells.map(ch => m(ch))
                ] ),
            );
        } else {
            return this.viewLayout(
                m("form.representation-"+this.representation, {}, [
                    this.viewGroup( this.sliderGroups[this.currentGroup] ),
                ]),
            );
        }

    }

}
